import React, { useEffect, useState } from 'react';
import styles from './card-user-info.module.scss';
import clsx from 'clsx';
import { Avatar, BottomSheet, Button, Container, Tag, Typography } from '@/ui-kit';
import { Profile } from '@/types/profile';
import { differenceInYearsResult } from '@/utils/difference-in-years-result';
import { LocationIcon, MagicIcon, OfflineStatus, OnlineStatus, PremiumIcon, VerifiedAccountIcon } from '@/assets/icons';
import { ButtonsLikeAndDislike } from '@/components/buttons-like-and-dislike';
import { dataPurposes } from '@/constans/purposes';
import { PremiumBottomsheet } from '@/components/premium-bottomsheet';
import { getProfileNameAndAge, travelPlansLabel } from '@/utils/msgs';

type Dir = 'left' | 'right';

interface Props {
    currentSwipeSlide: Profile;
    isVisibleDetails: boolean;
    detailsRef: React.RefObject<HTMLDivElement>;
    onSwipeLeft: (val: Dir) => void;
    onSwipeRight: (val: Dir) => void;
}

export const CardUserInfo: React.FC<Props> = ({
    currentSwipeSlide,
    isVisibleDetails,
    detailsRef,
    onSwipeLeft,
    onSwipeRight,
}) => {
    const currentPurpose = dataPurposes.find((purpose) => purpose.value === currentSwipeSlide?.purpose);

    const getIconComponent = (icon: React.FC<React.SVGProps<SVGSVGElement>>): React.ReactNode => {
        const IconComponent = icon;

        return <IconComponent />;
    };

    useEffect(() => {
        console.log(currentSwipeSlide);
    }, [currentSwipeSlide]);

    const [isVerificationModalOpen, setIsVerificationModalOpen] =
        useState(false);

    const [isPremiumModalOpen, setIsPremiumModalOpen] =
        useState(false);

    return (
        <div className={clsx(styles.descriptionWrapper, isVisibleDetails && styles.visibleDetails)}>
            <Container className={styles.infoWrapper}>
                <div ref={detailsRef} />
                <div className={styles.userInfo__header}>
                    <Typography
                        typeColor="black"
                        fontWeight="medium"
                        variant="span"
                        className={clsx(styles.city)}
                    >
                        {currentSwipeSlide?.is_online ? (
                            <>
                                <OnlineStatus /> Онлайн
                            </>
                        ) : (
                            <>
                                <OfflineStatus /> Оффлайн
                            </>
                        )}
                    </Typography>

                    {currentSwipeSlide?.regions ? (
                        currentSwipeSlide?.regions.map((region) => (
                            <Typography
                                typeColor="black"
                                fontWeight="medium"
                                variant="span"
                                className={clsx(styles.city)}
                            >
                                <LocationIcon /> {region?.name}
                            </Typography>
                        ))
                    ) : (
                        <Typography
                            typeColor="black"
                            fontWeight="medium"
                            variant="span"
                            className={clsx(styles.city)}
                        > Список локаций пуст</Typography>
                    )}
                </div>
                {currentSwipeSlide?.public_name && (
                    <div className={styles.nameAndVerificationStatusInfo}>
                        <Typography
                            typeColor="black"
                            fontWeight="medium"
                            variant="h3"
                            className={styles.cardInfoText}>
                            {getProfileNameAndAge(currentSwipeSlide)}
                        </Typography>
                        {
                            currentSwipeSlide
                                ?.verification_status
                                === 'verified' &&
                            <VerifiedAccountIcon
                                width={24}
                                height={24}
                                className={styles.verificationStatusInfo}
                                onClick={() => setIsVerificationModalOpen(true)} />
                        }
                        {
                            currentSwipeSlide.is_paid &&
                            <PremiumIcon
                                width={24}
                                height={24}
                                onClick={() => setIsPremiumModalOpen(true)} />
                        }
                    </div>
                )}

                {currentSwipeSlide?.about_me && (
                    <>
                        <div className={styles.label}>
                            <Typography
                                variant="label"
                                fontWeight="normal"
                                fontFamily="SFProText"
                            >
                                Обо мне
                            </Typography>
                        </div>

                        <Typography
                            className={styles.text}
                            variant="p"
                        >
                            {currentSwipeSlide?.about_me}
                        </Typography>
                    </>
                )}

                {currentPurpose && (
                    <Tag
                        className={clsx(styles.tagInfo, styles.isIcon)}
                        icon={getIconComponent(currentPurpose.icon)}
                    >
                        <div>
                            <Typography
                                className={styles.tagInfo__title}
                                variant="p"
                            >
                                Я ищу
                            </Typography>
                            <Typography
                                className={styles.tagInfo__text}
                                variant="p"
                                fontWeight="bold"
                            >
                                {currentPurpose.text}
                            </Typography>
                        </div>
                    </Tag>
                )}

                {currentSwipeSlide?.travel_plans && (
                    <>
                        <div className={styles.label}>
                            <Typography
                                variant="label"
                                fontWeight="normal"
                                fontFamily="SFProText">
                                {
                                    travelPlansLabel
                                }
                            </Typography>
                        </div>

                        <Typography
                            className={styles.text}
                            variant="p"
                        >
                            {currentSwipeSlide?.travel_plans}
                        </Typography>
                    </>
                )}

                <ButtonsLikeAndDislike
                    onSwipeLeft={() => onSwipeLeft('left')}
                    onSwipeRight={() => onSwipeRight('right')}
                    visible={true}
                    customClass={styles.actionButtons} />
            </Container>
            <BottomSheet
                overlayClassName={styles.bottomsheetOverlay}
                isOpen={isVerificationModalOpen}
                isDeskorTop={false}
                onClose={() => setIsVerificationModalOpen(false)}>
                <div className={styles.verificationModalContent}>
                    <div className={styles.verificationModalContentIcon}>
                        <Avatar
                            src={currentSwipeSlide.photos.length ? currentSwipeSlide.photos[0].main_url : null}
                            size={96} />
                        <VerifiedAccountIcon
                            className={styles.verificationModalContentIconVerification}
                            width={28}
                            height={28} />
                    </div>
                    <div className={styles.verificationModalContentHeader}>
                        <Typography
                            className={styles.verificationModalContentHeaderTitle}
                            variant={'h5'}
                            fontWeight={'bold'}>
                            {currentSwipeSlide.public_name} {currentSwipeSlide.gender === 'male' ? 'прошёл' : 'прошла'} проверку
                        </Typography>
                        <Typography
                            className={styles.verificationModalContentHeaderDesc}>
                            Видео-верификация подтверждает, что за анкетой стоит реальный человек и что фото принадлежат ему. Это помогает сделать общение честнее и безопаснее.
                        </Typography>
                    </div>
                    <div className={styles.verificationModalContentFootnote}>
                        <Button
                            typeColor="orange"
                            className={styles.verificationModalContentFootnoteBtn}
                            onClick={() => setIsVerificationModalOpen(false)}>
                            Понятно!
                        </Button>
                    </div>
                </div>
            </BottomSheet>

            <PremiumBottomsheet
                photo={currentSwipeSlide.photos.length ? currentSwipeSlide.photos[0].main_url : null}
                name={currentSwipeSlide.public_name}
                isPremiumModalOpen={isPremiumModalOpen}
                setIsPremiumModalOpen={setIsPremiumModalOpen} />
        </div>
    );
};
